import AddActionModal from 'modules/automation-rules/actions/components/add-action-modal'
import EditActions from 'modules/automation-rules/actions/components/edit-actions'
import { getDefaultActionValue } from 'modules/automation-rules/actions/utils/get-default-action-value'
import AddTriggerModal from 'modules/automation-rules/triggers/components/add-trigger-modal'
import EditTriggers, {
  EditTriggersProps,
} from 'modules/automation-rules/triggers/components/edit-triggers'
import { AutomationRuleTriggersEnum } from 'modules/automation-rules/triggers/enums/trigger-enum'
import { AutomationRulesTriggersEditType } from 'modules/automation-rules/triggers/types/automation-rule-triggers-edit-interface'
import { getDefaultTriggerValue } from 'modules/automation-rules/triggers/utils/get-default-trigger-value'
import {
  AutomationRuleErrorInterface,
  AutomationRuleViewInterface,
} from 'modules/automation-rules/types/automation-rule-interface'
import React, { Dispatch, SetStateAction } from 'react'
import { Loader } from 'shared/components/loader'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { AutomationRuleActionIcon, AutomationRuleTriggerIcon } from '@systemeio/ui'

interface RuleProps extends Pick<EditTriggersProps, 'hideTriggers'> {
  tempState: AutomationRuleViewInterface
  setTempState: Dispatch<SetStateAction<AutomationRuleViewInterface>>
  errors: AutomationRuleErrorInterface
  maxTriggerId: React.MutableRefObject<number>
  maxActionId: React.MutableRefObject<number>
  isPreFetching?: boolean
  isFetching: boolean
  customAddTriggerHandler?: (
    trigger: AutomationRuleTriggersEnum,
    maxTriggerId: React.MutableRefObject<number>,
  ) => AutomationRulesTriggersEditType
  customTriggerTitle?: React.ReactNode
  allowedTriggers?: AutomationRuleTriggersEnum[]
  isStaticTriggers?: boolean
}

export function Rule({
  isPreFetching,
  isFetching,
  maxActionId,
  maxTriggerId,
  tempState,
  setTempState,
  errors,
  customAddTriggerHandler,
  customTriggerTitle,
  allowedTriggers,
  isStaticTriggers,
  hideTriggers,
}: RuleProps) {
  const { t } = useLocoTranslation()
  const canAdd = !isFetching && !isPreFetching
  return (
    <div className={'overflow-x-hidden lg:rounded-lg'}>
      <div className={'flex flex-col gap-5 lg:flex-row lg:gap-0'}>
        <div
          className={
            'border-text-secondary/30 bg-background-secondary flex w-full flex-col rounded-lg lg:w-1/2 lg:rounded-none lg:border-r'
          }
        >
          <div className={'bg-background-primary/50 px-20 py-5'}>
            <div className={'flex justify-between gap-5'}>
              <div className={'flex items-center gap-3'}>
                <AutomationRuleTriggerIcon />
                <div className={'flex flex-col gap-1'}>
                  {customTriggerTitle ?? (
                    <span className={'text-text-primary text-[17px] font-bold'}>
                      {t('dashboard.automation_rule.trigger.title')}
                    </span>
                  )}
                  <span className={'text-text-primary'}>
                    {t('dashboard.automation_rule.edit.when_happens')}
                  </span>
                </div>
              </div>
              <AddTriggerModal
                allowedTriggers={allowedTriggers}
                isReady={canAdd}
                onAddTrigger={trigger =>
                  setTempState(prev => {
                    return {
                      ...prev,
                      triggers: [
                        ...prev.triggers,
                        customAddTriggerHandler
                          ? customAddTriggerHandler(trigger, maxTriggerId)
                          : getDefaultTriggerValue(trigger, maxTriggerId),
                      ],
                    }
                  })
                }
              />
            </div>
          </div>
          <div
            className={`relative flex min-h-[500px] flex-col gap-16 px-20 py-5 ${
              isPreFetching && 'justify-center'
            }`}
          >
            {isPreFetching ? (
              <div className={'flex h-full items-center justify-center'}>
                <Loader className={'scale-150'} />
              </div>
            ) : (
              <EditTriggers
                isStatic={isStaticTriggers}
                hideTriggers={hideTriggers}
                errors={errors}
                setRules={setTempState}
                triggers={tempState.triggers}
              />
            )}
            <div
              className={
                'bg-background-secondary border-text-secondary/30 absolute inset-x-full inset-y-1/2 hidden h-[30px] w-[30px] -translate-x-1/2 -translate-y-1/2 rotate-45 border-r border-t lg:inline'
              }
            ></div>
          </div>
        </div>
        <div
          className={
            'bg-background-secondary flex w-full flex-col rounded-lg lg:w-1/2 lg:rounded-none'
          }
        >
          <div className={'bg-background-primary/50 px-20 py-5'}>
            <div className={'flex justify-between gap-5'}>
              <div className={'flex items-center gap-3'}>
                <AutomationRuleActionIcon />
                <div className={'flex flex-col gap-1'}>
                  <span className={'text-text-primary text-[17px] font-bold'}>
                    {t('dashboard.automation_rule.action.title')}
                  </span>
                  <span className={'text-text-primary'}>
                    {t('dashboard.automation_rule.edit.then_happens')}
                  </span>
                </div>
              </div>
              <AddActionModal
                isReady={canAdd}
                onAddAction={action =>
                  setTempState(prev => {
                    return {
                      ...prev,
                      actions: [
                        ...prev.actions,
                        {
                          ...getDefaultActionValue(action, maxActionId),
                        },
                      ],
                    }
                  })
                }
              />
            </div>
          </div>
          <div
            className={`relative flex min-h-[500px] flex-col gap-16 px-20 py-5 ${
              isPreFetching && 'justify-center'
            }`}
          >
            {isPreFetching ? (
              <div className={'flex h-full items-center justify-center'}>
                <Loader className={'scale-150'} />
              </div>
            ) : (
              <EditActions errors={errors} setRules={setTempState} actions={tempState.actions} />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
